<template>
  <section
    :key="forceUpdateCollection"
    class="d-flex d-print-none"
    :aria-roledescription="banners.length > 1 ? 'carousel' : null"
  >
    <div
      ref="carousel"
      :key="banners.length"
      class="fipa-carousel ma-auto"
      :class="{
        'takeover-mode': takeoverBannerMode
      }"
      :style="styleCss"
    >
      <div
        v-if="enableUserControl"
        v-show="banners.length > 0"
        class="fipa-carousel-control__left"
        @click="previousSlide"
      >
        <v-icon color="white" large>$iconCircleCaretLeft</v-icon>
      </div>
      <div
        v-for="(b, i) in banners"
        :key="i"
        :class="[
          i == visible
            ? 'fipa-carousel-content__active-slider'
            : 'fipa-carousel-content__inactive-slider'
        ]"
        :data-banner-index="i"
        class="fipa-carousel-content"
        :aria-roledescription="banners.length > 1 ? 'slide' : null"
      >
        <div
          v-if="b.contentType == 'EXTERNAL_ADNUNTIUS'"
          :class="'adn-' + b.contentText.split('_')[1]"
        />
        <div
          v-else-if="b.contentType == 'EXTERNAL_NIF'"
          :id="getNIFMeta(b).placementId"
          style="display: none"
        />
        <component
          :is="b.targetUrl ? 'a' : 'span'"
          v-else
          :href="b.targetUrl ? b.targetUrl : null"
          :target="b.targetUrl ? '_blank' : null"
          @click="userClickedBanner(b)"
        >
          <picture>
            <source :srcset="b.imageUrl" />
            <img
              :src="b.imageUrl"
              :style="styleImage"
              style="object-fit: cover; object-position: center"
              :alt="b.title"
            />
          </picture>
        </component>
      </div>
      <div
        v-if="enableUserControl"
        v-show="banners.length > 0"
        class="fipa-carousel-control__right"
        @click="nextSlide"
      >
        <v-icon color="white" large>$iconCircleCaretRight</v-icon>
      </div>
    </div>
  </section>
</template>
<script>
import { useAdnuntius } from "./adnuntius";
import { useNIF } from "./nif";

export default {
  name: "AppBannerCollection",
  props: {
    banners: {
      type: Array,
      default: () => []
    },
    minWidth: {
      type: String,
      default: null
    },
    width: {
      type: String,
      default: null
    },
    maxWidth: {
      type: String,
      default: "100%"
    },
    height: {
      type: String,
      default: null
    },
    minHeight: {
      type: String,
      default: undefined
    },
    maxHeight: {
      type: String,
      default: "100%"
    },
    styleImage: {
      type: String,
      default: null
    },
    randomizeStart: {
      type: Boolean,
      default: true
    },
    enableUserControl: {
      type: Boolean,
      default: false
    },
    takeoverBannerMode: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { adnuntiusFetch } = useAdnuntius();
    const { nifFetch, getNIFMeta } = useNIF();
    return { adnuntiusFetch, nifFetch, getNIFMeta };
  },
  data() {
    return {
      forceUpdateCollection: 0,
      visible: 0,
      nextSlideTimeout: null,
      slideInAction: false,
      observer: null
    };
  },
  computed: {
    carouselBannersLength() {
      return this.banners.length;
    },
    styleCss() {
      if (this.takeoverBannerMode) return undefined;

      const width = this.width ? `width: ${this.width}` : null;
      const minWidth = this.minWidth ? `min-width: ${this.minWidth}` : null;
      const maxWidth = this.maxWidth ? `max-width: ${this.maxWidth}` : null;

      const height = this.height ? `height: ${this.height}` : null;
      const minHeight = this.minHeight ? `min-height: ${this.minHeight}` : null;
      const maxHeight = this.maxHeight ? `max-height: ${this.maxHeight}` : null;

      return [width, maxWidth, minWidth, height, minHeight, maxHeight]
        .filter(Boolean)
        .join(";");
    }
  },
  watch: {
    "$vuetify.breakpoint.lgAndUp"() {
      if (this.banners.length > 1) {
        this.prepareCarousel(this.banners);
      }
    }
  },
  mounted() {
    this.nifFetch(this.banners);
    this.adnuntiusFetch(this.banners, {
      maxHeight: this.maxHeight,
      maxWitdh: this.maxWidth
    });
    this.prepareCarousel(this.banners);
  },
  activated() {
    this.nifFetch(this.banners);
    this.adnuntiusFetch(this.banners, {
      maxHeight: this.maxHeight,
      maxWitdh: this.maxWidth
    });
    this.prepareCarousel(this.banners);
  },
  deactivated() {
    clearTimeout(this.nextSlideTimeout);
    if (this.observer) this.observer.disconnect();
    // Re-render component back to initial state for adnuntius
    // otherwise other rows with exact same banners wont render again
    this.forceUpdateCollection++;
  },
  beforeDestroy() {
    clearTimeout(this.nextSlideTimeout);
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    async prepareCarousel(banners) {
      clearTimeout(this.nextSlideTimeout);
      this.visible = 0;
      this.slideInAction = false;
      if (banners.length > 1) {
        if (this.randomizeStart) {
          this.visible = Math.floor(Math.random() * Math.floor(banners.length));
        }
        this.setIntervalCarousel(banners[this.visible].rotateAfterSeconds);
      }

      this._setIntersectionViewed();
    },
    _setIntersectionViewed() {
      if (this.observer) this.observer.disconnect();
      this.observer = new IntersectionObserver(
        entries => {
          const entry = entries[0];
          if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
            this.observer.unobserve(entry.target);
            if (process.env.VUE_APP_GOOGLE_ANALYTICS_TRACKING_ID) {
              const b =
                this.banners[
                  entry.target.closest("[data-banner-index]").dataset
                    .bannerIndex
                ];
              if (this.$gtag) {
                this.$gtag.event("banner_view", {
                  placement: b.placement,
                  position: b.position,
                  title: b.title,
                  customer: b.customerName,
                  content_type: b.contentType
                });
              }
            }
          }
        },
        {
          threshold: 0.35
        }
      );
      Array.from(this.$el.querySelectorAll("img")).forEach(el => {
        this.observer.observe(el);
      });
    },
    userClickedBanner(b) {
      if (process.env.VUE_APP_GOOGLE_ANALYTICS_TRACKING_ID) {
        this.$gtag.event("banner_click", {
          placement: b.placement,
          position: b.position,
          title: b.title,
          customer: b.customerName,
          content_type: b.contentType
        });
      }
    },
    setIntervalCarousel(bannerRotateSeconds) {
      clearTimeout(this.nextSlideTimeout);
      if (!bannerRotateSeconds) return;
      if (this.banners.length <= 1) return;
      this.nextSlideTimeout = setTimeout(() => {
        this.nextSlide();
      }, bannerRotateSeconds * 1000);
      // Wants in milliseconds
    },
    mouseoverCarousel() {
      this.$refs.carousel.classList.add("fipa-carousel-hovered");
    },
    mouseoutCarousel() {
      this.setIntervalCarousel();
      this.$refs.carousel.classList.remove("fipa-carousel-hovered");
    },
    nextSlide() {
      if (this.slideInAction) return false;

      this.slideInAction = true;
      const nextSlideIndex =
        this.carouselBannersLength > this.visible + 1 ? this.visible + 1 : 0;

      const nextSlide = this.$el.querySelector(
        `.fipa-carousel div.fipa-carousel-content[data-banner-index="${nextSlideIndex}"]`
      );
      if (!nextSlide) return false;
      nextSlide.addEventListener(
        "animationend",
        () => {
          this.visible = nextSlideIndex;
          nextSlide.classList.remove("fipa-carousel-content__slide-in-right");

          this.setIntervalCarousel(
            this.banners[nextSlideIndex].rotateAfterSeconds
          );
          this.slideInAction = false;
        },
        { once: true, passive: true }
      );

      nextSlide.classList.add("fipa-carousel-content__slide-in-right");
    },
    previousSlide() {
      if (this.slideInAction) return false;
      this.slideInAction = true;
      const prevSlideIndex =
        0 <= this.visible - 1
          ? this.visible - 1
          : this.carouselBannersLength - 1;

      const prevSlide = this.$el.querySelector(
        `.fipa-carousel div.fipa-carousel-content[data-banner-index="${prevSlideIndex}"]`
      );

      prevSlide.addEventListener(
        "animationend",
        () => {
          this.visible = prevSlideIndex;
          prevSlide.classList.remove("fipa-carousel-content__slide-in-left");
          this.setIntervalCarousel(
            this.banners[prevSlideIndex].rotateAfterSeconds
          );
          this.slideInAction = false;
        },
        { once: true, passive: true }
      );

      prevSlide.classList.add("fipa-carousel-content__slide-in-left");
    }
  }
};
</script>
<style scoped lang="scss">
@keyframes slide-in-left {
  0% {
    transform: translateX(-2vw);
    opacity: 0.65;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slide-in-right {
  0% {
    transform: translateX(2vw);
    opacity: 0.65;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
}

.fipa-carousel {
  position: relative;

  &.takeover-mode {
    position: fixed;
    z-index: 50000;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
  }

  @media (max-width: 768px) {
    //margin-bottom: 24px !important;
  }
  &-content {
    will-change: transformX opacity;

    &.fipa-carousel-content__active-slider {
      display: flex;
    }

    &.fipa-carousel-content__inactive-slider {
      display: none;
    }

    &.fipa-carousel-content__slide-in-left,
    &.fipa-carousel-content__slide-in-right {
      position: absolute;
      z-index: 1;
      top: 0;
      display: block;
      animation-duration: 0.2s;
      animation-timing-function: linear;
    }

    &.fipa-carousel-content__slide-in-left {
      animation-name: slide-in-left;
    }

    &.fipa-carousel-content__slide-in-right {
      animation-name: slide-in-right;
    }

    img {
      max-width: 100%;
      display: block;
    }
  }
  /*
  &-control {
    $width: 4vw;

    @mixin control-btn() {
      position: absolute;
      top: 0;
      bottom: 0;
      width: $width;
      height: 100%;
      z-index: 10;
      will-change: transform;
      transition: transform cubic-bezier(0.79, 0.14, 0.15, 0.86) 0.25s;

      display: flex;
      align-items: center;
      cursor: pointer;
    }

    &__left {
      @include control-btn;
      transform: translateX(-$width);

      left: 0;

      background: rgba(0, 0, 0, 0.65);

      & > .v-icon {
        margin-left: 20px;
      }
    }
    &__right {
      @include control-btn;

      transform: translateX($width);
      right: 0;

      background: rgba(0, 0, 0, 0.65);

      justify-content: flex-end;

      & > .v-icon {
        margin-right: 20px;
      }
    }
  }*/
}
</style>
